




































































































































import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import DynamicForm from '@/components/shared/DynamicForm.vue';
import MStoreDocumentTypeSelect from '@/components/shared/MStoreDocumentTypeSelect.vue';
import DocumentViewer from '@/components/shared/DocumentViewer.vue';
import MStorePageRange from '@/components/shared/MStorePageRange.vue';
import MStoreThumbnailFirstLastView from '@/components/shared/MStoreThumbnailFirstLastView.vue';
import ErrorNotification from '@/components/shared/ErrorNotification.vue';
import CustomIndexingLookups from '@/components/documentToStore/CustomIndexingLookups.vue';
import {
  DocumentToStoreSearchModel,
  DocumentToStoreViewModel,
  SchemaModel,
  DocumentToStoreUpdateModel,
  DocumentViewerToolbarOptionsModel,
  PagesStoreMode,
  DocumentPageModel,
  DocumentViewerConstants,
  ErrorCode,
  EditReferencesRouteTypes
} from '@/types';
import { DocumentToStoreService } from '@/services';
import LoadingSpinner from '@/components/shared/LoadingSpinner.vue';
import { ArrayUtils } from '@/utils';
import { confirmationHelper, FormFieldValidation, StoreDocumentHelpers } from '@/mixins';
import MStoreDescriptionButton from '@/components/shared/MStoreDescriptionButton.vue';
import { RouterUtils } from '@/utils';

export default Vue.extend({
  name: 'DefaultEditReferences',
  mixins: [StoreDocumentHelpers, confirmationHelper],
  data() {
    return {
      lastActivity: new Date(),
      pageRangePreviousPageNavigationIsEnabled: false,
      pageRangeSelectAllPages: false,
      exportOptions: DocumentViewerConstants.WithoutAcquiringLockExportOptions,
      isMultiValuesUpdating: false
    };
  },
  methods: {
    ...mapActions('editReferencesStore', [
      'setDocumentToStoreView',
      'isLoading',
      'selectExistingDocument',
      'clearExistingDocument',
      'setSourceDocumentTypeId',
      'setPagesStoreMode',
      'setTargetDocumentTypeId',
      'releaseDocumentToStoreLock',
      'setSelectedPages'
    ]),
    ...mapActions('documentViewStore', ['showLoadingSpinner', 'removeCachedPagesForDocument', 'updatePageCount', 'setDocumentSearchModel']),
    ...mapActions('applicationDirtyStore', ['setApplicationDirty']),
    async getDocumentToStoreViewAsyncWithErrorPage(this: any, reloadFromApi: boolean): Promise<DocumentToStoreViewModel> {
      if (!reloadFromApi && this.documentToStoreView.cabinets.length && this.documentToStoreView.dynamicFormSchema.length) {
        return this.documentToStoreView;
      }

      const result = await DocumentToStoreService.getDocumentToStoreViewAsync({
        sourceCabinetId: this.sourceCabinetId,
        sourceDocumentId: this.sourceDocumentId,
        targetDocumentTypeId: this.targetDocumentTypeId
      } as DocumentToStoreSearchModel);

      if (result) {
        if (this.targetDocumentTypeId !== result.targetDocumentTypeId) {
          this.setTargetDocumentTypeId(result.targetDocumentTypeId);
        }
        this.setDocumentToStoreView(result);
      }
      return result;
    },
    async initialiseDocumentToStoreView(this: any, loadDocument: boolean, reloadFromApi: boolean) {
      this.isPageLoading = true;
      this.getRetainedReferences();
      try {
        await this.getDocumentToStoreViewAsyncWithErrorPage(reloadFromApi);
        if (!this.isComponentAlive()) {
          return;
        }
        if (this.previousTargetCabinetId === this.targetCabinetId) {
          this.applyRetainedReferences();
        }
        if (loadDocument) {
          this.loadDocumentPreview();
        }
      } catch (error) {
        this.$log(error);
      } finally {
        this.isPageLoading = false;
      }
    },
    async onStorePagesClicked(this: any) {
      const isFormValid = await this.validateForm();
      if (!isFormValid) {
        return;
      }

      const allPagesSelectedForStore = this.allPagesAreSelectedForStore;

      const success = await this.storePages();
      if (!success) {
        return;
      }

      this.setApplicationDirty(false);

      if (allPagesSelectedForStore) {
        this.goBackToPreviousLocation();
        return;
      }

      if (this.clearGridAfterUpdate) {
        this.clearReferenceValues();
      } else if (this.clearDocumentDate) {
        this.setDocumentDateValue(this.initialDocumentDateValue);
      }

      this.updateStateAfterPagesStored();

      this.setFocusAfterPagesStored();
    },
    async validateForm(this: any): Promise<boolean> {
      const isDynamicFormValid = await this.$refs.validationObserver.validate();
      if (!isDynamicFormValid) {
        FormFieldValidation.methods.setFocusOnInvalidField(this);
        return false;
      }
      return true;
    },
    async storePages(this: any): Promise<boolean> {
      this.isSubmitLoading = true;
      try {
        const pages = this.document.viewConversionId || this.document.hasNotification ? null : this.selectedPages;
        const storeModel: DocumentToStoreUpdateModel = {
          targetCabinetId: this.targetCabinetId,
          targetDocumentTypeId: this.selectedTargetDocumentTypeId,
          references: this.referenceValues,
          multiValueReferences: this.multiValueReferences,
          pages,
          existingDocumentId: this.existingDocumentId,
          existingDocumentOption: this.existingDocumentOption,
          documentDate: this.documentDateReferenceValue
        };

        const result = await DocumentToStoreService.storeUpdateDocumentAsync(this.sourceCabinetId, this.sourceDocumentId, storeModel);

        if (result.errorCode === ErrorCode.DocumentPageShuffleSucceededReIndexFailed) {
          this.shuffleSucceededReIndexFailed();
          return false;
        }
      } catch (error) {
        this.$log(error);
        return false;
      } finally {
        this.isSubmitLoading = false;
      }

      return true;
    },
    shuffleSucceededReIndexFailed(this: any) {
      this.initialiseDocumentToStoreView(true, false);
      this.selectedPages = `1-${this.document.totalPages}`;
      this.removeCachedPages();
    },
    onCloseButtonClicked(this: any) {
      this.goBackToPreviousLocation();
    },
    async goBackToPreviousLocation(this: any) {
      let hasUnsavedChangesToKeep = false;
      if (this.isApplicationDirty) {
        hasUnsavedChangesToKeep = await this.hasUnsavedChangesToKeepAsync(this.$bvModal, true);
      }
      if (hasUnsavedChangesToKeep) {
        return;
      }
      this.setApplicationDirty(false);
      const returnUrl = sessionStorage.getItem(EditReferencesRouteTypes.ReturnUrl);

      if (returnUrl) {
        window.location.href = returnUrl;
      } else {
        const returnRoute = sessionStorage.getItem(EditReferencesRouteTypes.ReturnRoute);
        const routeWithParameterValues = RouterUtils.replaceRouteParameterPlaceholdersWithValues(returnRoute, { cabinetId: this.targetCabinetId, documentId: this.sourceDocumentId });
        this.$router.push({
          path: routeWithParameterValues || '/'
        });
      }
    },
    onClearReferences(this: any): void {
      if (this.isPageLoading || this.documentDetailsReadonly) {
        return;
      }
      this.clearReferenceValues();
      this.setFocusToFirstReferenceField();
    },
    setFocusToFirstReferenceField(this: any) {
      this.$nextTick(() => {
        const visibleFields = this.referenceFields.filter((field: any) => !field.componentProperties.hidden);
        if (visibleFields && visibleFields.length) {
          const field: SchemaModel = visibleFields[0];
          this.$refs.dynamicForm.setFocusOnField(field);
        }
      });
    },
    removeCachedPages(this: any) {
      const model = {
        documentId: this.sourceDocumentId,
        pages: this.selectedPageRange
      };
      this.removeCachedPagesForDocument(model);
    },
    updateStateAfterPagesStored(this: any) {
      if (this.selectedPageRange?.length) {
        this.removeCachedPages();
        this.updatePageCount(-this.selectedPageRange?.length);

        let pageToPreview = Math.min(this.currentPage, this.document.totalPages);

        if (this.document.totalPages === 1 || this.pagesStoreMode) {
          this.selectedPages = '1';
          pageToPreview = 1;
        }

        this.loadPage(pageToPreview);
      }
    },
    onCabinetSelectionChanged(this: any, cabinetId: string) {
      const documentTypeId = this.firstDocumentTypeInCabinet(cabinetId);
      this.setTargetDocumentTypeId(documentTypeId);
      this.clearExistingDocument();
      this.initialiseDocumentToStoreView(false, true);
    },
    setFocusAfterPagesStored(this: any) {
      if (this.isFocusToDocumentTypeAfterStore && this.cabinetDocumentTypesCount(this.targetCabinetId) > 1) {
        this.$refs.documentTypeSelect.setFocusToDocumentTypeForSelection();
      } else {
        this.setFocusToFirstReferenceField();
      }
    }
  },
  computed: {
    ...mapGetters('editReferencesStore', [
      'errorModel',
      'referenceValues',
      'documentDateReferenceValue',
      'isReadonly',
      'existingDocumentId',
      'existingDocumentOption',
      'targetCabinetId',
      'sourceDocumentTypeId',
      'initialReferenceValues',
      'initialDocumentDateValue',
      'referenceFields',
      'customIndexingLookups',
      'getCabinetIdFromDocumentType',
      'firstDocumentTypeInCabinet',
      'getPagesStoreMode',
      'clearDocumentDate',
      'cabinetDocumentTypesCount',
      'isFocusToDocumentTypeAfterStore',
      'pageViewedBeforeAddToExisting',
      'getSelectedPages',
      'getPagesStoreMode',
      'documentToStore'
    ]),
    ...mapGetters('documentViewStore', ['currentPage', 'deletedPage', 'deletedDocumentId', 'isTifOrPdf']),
    ...mapGetters('applicationDirtyStore', ['isApplicationDirty']),
    sourceDocumentId: {
      get(this: any) {
        return Number(this.$route.params.sourceDocumentId);
      }
    },
    sourceCabinetId: {
      get(this: any) {
        return this.$route.params.sourceCabinetId;
      }
    },
    selectedTargetDocumentTypeId: {
      get(this: any) {
        return this.targetDocumentTypeId;
      },
      set(this: any, value: number) {
        if (this.targetCabinetId) {
          this.previousTargetCabinetId = this.targetCabinetId;
        }
        if (this.selectedTargetDocumentTypeId !== value) {
          this.setTargetDocumentTypeId(value);
          this.clearExistingDocument();
          if (value) {
            this.initialiseDocumentToStoreView(false, true);
          }
        }
      }
    },
    isStoreButtonDisabled(this: any): boolean {
      return !this.targetDocumentTypeId || !this.isSelectedPageRangeValid || this.isSubmitLoading || this.isPageLoading || this.isMultiValuesUpdating;
    },
    isSelectedPageRangeValid(this: any): boolean {
      return (this.isPageRangeIsValid && this.selectedPages) || this.document.viewConversionId || this.document.hasNotification;
    },
    thumbnailHeader(this: any): string {
      const pages = this.selectedPageRange?.length || this.document?.totalPages;
      return `${pages} page${pages > 1 ? 's' : ''} to update`;
    },
    documentViewerToolbarOptions(this: any): DocumentViewerToolbarOptionsModel {
      const options: DocumentViewerToolbarOptionsModel = {
        allowPageNavigation: true,
        allowPageDelete: !this.documentViewerDeleteIsDisabled,
        allowDocumentDelete: !this.documentViewerDeleteIsDisabled,
        pageDeleteNotAllowedTooltip: '',
        documentDeleteNotAllowedTooltip: '',
        showEditReferences: false
      };
      return options;
    },
    pageRangeIsDisabled(this: any): boolean {
      return !this.isPageSplittingSupported || this.isSubmitLoading;
    },
    updateButtonText(this: any): string {
      const defaultText = 'Update';
      if (!this.selectedPageRange || !this.isPageSplittingSupported) {
        return defaultText;
      }
      return `${defaultText} ${this.selectedPageRange.length} ${this.selectedPageRange.length === 1 ? 'page' : 'pages'}`;
    },
    documentDetailsReadonly(this: any) {
      return this.isSubmitLoading;
    },
    selectedPages: {
      get(this: any) {
        return this.getSelectedPages;
      },
      set(this: any, value: any) {
        this.setSelectedPages(value);
      }
    },
    pagesStoreMode: {
      get(this: any) {
        return this.getPagesStoreMode;
      },
      set(this: any, value: PagesStoreMode) {
        this.setPagesStoreMode(value);
      }
    }
  },
  watch: {
    pagesStoreMode(this: any, newValue: number, oldValue: number) {
      this.pageRangePreviousPageNavigationIsEnabled = false;
      if (newValue === PagesStoreMode.ManualPageSelection) {
        this.pageRangeSelectAllPages = true;
      }

      if (oldValue !== PagesStoreMode.NoSplitting) {
        this.loadPage(1);
      }
    },
    hasDocumentLoaded(this: any, value: boolean) {
      if (value) {
        if (this.pagesStoreMode !== PagesStoreMode.NoSplitting) {
          return;
        } else {
          this.pagesStoreMode = this.isPageSplittingSupported ? PagesStoreMode.ManualPageSelection : PagesStoreMode.NoSplitting;
        }
      }
    },
    showDocumentCannotBeSplit(this: any, value: boolean) {
      if (value) {
        if (!this.isPageSplittingSupported) {
          this.pageRangeSelectAllPages = true;
        }
      }
    },
    deletedPage(this: any, value: DocumentPageModel) {
      if (value && value.documentId === this.sourceDocumentId) {
        this.selectedPages = ArrayUtils.removePageFromSelection(this.selectedPages, value.pageNumber);
      }
    },
    deletedDocumentId(this: any, value: number) {
      if (value && value === this.sourceDocumentId) {
        this.goBackToPreviousLocation();
      }
    }
  },
  mounted(this: any) {
    this.setSourceDocumentTypeId(this.$route.params.sourceDocumentTypeId);
    if (!this.targetDocumentTypeId) {
      this.setTargetDocumentTypeId(this.$route.params.sourceDocumentTypeId);
    }
    this.initialiseDocumentToStoreView(true, false);
  },
  beforeDestroy(this: any) {
    if (!this.onAddToExistingNavigation) {
      this.initialisePageSelectionValues();
    }
  },
  components: {
    DynamicForm,
    ErrorNotification,
    MStoreDocumentTypeSelect,
    DocumentViewer,
    MStorePageRange,
    MStoreThumbnailFirstLastView,
    LoadingSpinner,
    CustomIndexingLookups,
    MStoreDescriptionButton,
    MultiValueReferencesInput: () => import('../documentToStore/MultiValueReferencesInput.vue')
  }
});
